/**
 * Toaster.tsx — Unified toast notification system for Kapi POS
 *
 * Usage:
 *   // Mount once in App.tsx:
 *   <Toaster />
 *
 *   // Use anywhere via hook:
 *   const { toast } = useToast();
 *   toast.success('Venta registrada');
 *   toast.error('Error al guardar');
 *   toast.warning('Sin conexión');
 *   toast.info('Sincronizando...');
 *
 * Variants: success ✅ | error ❌ | warning ⚠️ | info ℹ️
 * Auto-dismisses after 4s (configurable). Max 5 toasts on screen.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
  /** Duration in ms. 0 = persist until dismissed. Default: 4000 */
  duration?: number;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastAPI {
  success: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'message' | 'variant'>>) => string;
  error:   (message: string, options?: Partial<Omit<ToastItem, 'id' | 'message' | 'variant'>>) => string;
  warning: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'message' | 'variant'>>) => string;
  info:    (message: string, options?: Partial<Omit<ToastItem, 'id' | 'message' | 'variant'>>) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ToastContext = createContext<ToastAPI | null>(null);

// ---------------------------------------------------------------------------
// Provider + Toaster
// ---------------------------------------------------------------------------

const MAX_TOASTS = 5;
const DEFAULT_DURATION = 4000;

let idCounter = 0;
const genId = () => `toast-${Date.now()}-${++idCounter}`;

// ---------------------------------------------------------------------------
// Visual config per variant
// ---------------------------------------------------------------------------

const VARIANT_CONFIG: Record<ToastVariant, {
  icon: string;
  bg: string;
  border: string;
  text: string;
  iconColor: string;
  progressBg: string;
}> = {
  success: {
    icon: '✓',
    bg: '#f0fdf4',
    border: '#bbf7d0',
    text: '#14532d',
    iconColor: '#16a34a',
    progressBg: '#16a34a',
  },
  error: {
    icon: '✕',
    bg: '#fef2f2',
    border: '#fecaca',
    text: '#7f1d1d',
    iconColor: '#dc2626',
    progressBg: '#dc2626',
  },
  warning: {
    icon: '⚠',
    bg: '#fffbeb',
    border: '#fde68a',
    text: '#78350f',
    iconColor: '#d97706',
    progressBg: '#d97706',
  },
  info: {
    icon: 'ℹ',
    bg: '#eff6ff',
    border: '#bfdbfe',
    text: '#1e3a5f',
    iconColor: '#2563eb',
    progressBg: '#2563eb',
  },
};

// ---------------------------------------------------------------------------
// Individual toast component
// ---------------------------------------------------------------------------

interface ToastCardProps {
  item: ToastItem;
  onDismiss: (id: string) => void;
}

function ToastCard({ item, onDismiss }: ToastCardProps): ReactElement {
  const [progress, setProgress] = useState(100);
  const startTimeRef = useRef(Date.now());
  const duration = item.duration ?? DEFAULT_DURATION;
  const cfg = VARIANT_CONFIG[item.variant];

  useEffect(() => {
    if (!duration) return; // persistent
    startTimeRef.current = Date.now();

    const frame = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, ((duration - elapsed) / duration) * 100);
      setProgress(remaining);
      if (remaining > 0) {
        requestAnimationFrame(frame);
      }
    };

    const raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [duration]);

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: 8,
        padding: '12px 14px',
        minWidth: 280,
        maxWidth: 360,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        position: 'relative',
        overflow: 'hidden',
        animation: 'toastSlideIn 0.2s ease-out',
      }}
    >
      {/* Icon */}
      <span style={{
        flexShrink: 0,
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: cfg.iconColor,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        marginTop: 1,
      }}>
        {cfg.icon}
      </span>

      {/* Message + action */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0,
          fontSize: 13,
          lineHeight: '1.4',
          color: cfg.text,
          fontWeight: 500,
          wordBreak: 'break-word',
        }}>
          {item.message}
        </p>
        {item.action && (
          <button
            onClick={() => {
              item.action!.onClick();
              onDismiss(item.id);
            }}
            style={{
              marginTop: 6,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: cfg.iconColor,
              fontSize: 12,
              fontWeight: 600,
              textDecoration: 'underline',
            }}
          >
            {item.action.label}
          </button>
        )}
      </div>

      {/* Dismiss button */}
      <button
        onClick={() => onDismiss(item.id)}
        aria-label="Cerrar"
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          padding: 2,
          cursor: 'pointer',
          color: cfg.text,
          opacity: 0.5,
          fontSize: 16,
          lineHeight: 1,
          marginTop: -1,
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.5'; }}
      >
        ×
      </button>

      {/* Progress bar */}
      {!!duration && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: 3,
          background: cfg.progressBg,
          width: `${progress}%`,
          transition: 'width 0.1s linear',
          borderRadius: '0 0 0 8px',
          opacity: 0.6,
        }} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ToasterProvider — wrap app once
// ---------------------------------------------------------------------------

interface ToasterProviderProps {
  children: React.ReactNode;
}

export function ToasterProvider({ children }: ToasterProviderProps): ReactElement {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setItems([]);
  }, []);

  const add = useCallback((
    message: string,
    variant: ToastVariant,
    options?: Partial<Omit<ToastItem, 'id' | 'message' | 'variant'>>,
  ): string => {
    const id = genId();
    const duration = options?.duration ?? DEFAULT_DURATION;
    const item: ToastItem = { id, message, variant, duration, ...options };

    setItems((prev) => {
      const next = [...prev, item];
      // Keep max N toasts — remove oldest
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
    });

    if (duration) {
      setTimeout(() => dismiss(id), duration + 200);
    }

    return id;
  }, [dismiss]);

  const api: ToastAPI = {
    success:    (msg, opts) => add(msg, 'success', opts),
    error:      (msg, opts) => add(msg, 'error', { duration: 6000, ...opts }),
    warning:    (msg, opts) => add(msg, 'warning', opts),
    info:       (msg, opts) => add(msg, 'info', opts),
    dismiss,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* Toast portal — fixed bottom-right */}
      <div
        aria-label="Notificaciones"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        <style>{`
          @keyframes toastSlideIn {
            from { opacity: 0; transform: translateX(20px); }
            to   { opacity: 1; transform: translateX(0); }
          }
        `}</style>
        {items.map((item) => (
          <div key={item.id} style={{ pointerEvents: 'all' }}>
            <ToastCard item={item} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Toaster — convenience re-export for when you just need the component
// ---------------------------------------------------------------------------

export function Toaster(): null {
  // The actual rendering is done by ToasterProvider.
  // This no-op exists for backwards compat in case someone tries to mount
  // <Toaster /> separately — just make sure <ToasterProvider> wraps the app.
  return null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useToast(): ToastAPI {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Gracefully degrade if provider is missing
    const noop = () => '';
    return {
      success: noop,
      error:   noop,
      warning: noop,
      info:    noop,
      dismiss: () => { /* noop */ },
      dismissAll: () => { /* noop */ },
    };
  }
  return ctx;
}
